/**
 * ConfigOvpnSection (F24.2) — UI para configurar el `remote` que aparece en
 * los archivos `.ovpn` que descargan los routers. 3 keys:
 *   - ovpn.remote_host   (string, default admin.bytecom.mx)
 *   - ovpn.remote_port   (int, default 1194)
 *   - ovpn.remote_proto  (string, default udp)
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Globe, Info, Loader2, Save } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useConfigByGrupo, useUpdateConfig } from "@/features/config/api";
import type { ApiError } from "@/lib/api";

const KEY_HOST = "ovpn.remote_host";
const KEY_PORT = "ovpn.remote_port";
const KEY_PROTO = "ovpn.remote_proto";

export function ConfigOvpnSection() {
  const { data: items, isLoading } = useConfigByGrupo("ovpn");
  const updateMut = useUpdateConfig();

  const [host, setHost] = useState<string>("admin.bytecom.mx");
  const [port, setPort] = useState<string>("1194");
  const [proto, setProto] = useState<string>("udp");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (items) {
      const h = items.find((i) => i.key === KEY_HOST);
      const p = items.find((i) => i.key === KEY_PORT);
      const pr = items.find((i) => i.key === KEY_PROTO);
      setHost(String(h?.value ?? "admin.bytecom.mx"));
      setPort(String(p?.value ?? 1194));
      setProto(String(pr?.value ?? "udp"));
      setDirty(false);
    }
  }, [items]);

  async function handleSave() {
    const portNum = Number(port);
    if (!Number.isInteger(portNum) || portNum < 1 || portNum > 65535) {
      toast.error("Puerto inválido", { description: "Debe ser un entero entre 1 y 65535." });
      return;
    }
    if (!host.trim()) {
      toast.error("Host vacío", { description: "Ingresa un dominio o IP." });
      return;
    }
    if (proto !== "udp" && proto !== "tcp") {
      toast.error("Protocolo inválido", { description: "Solo udp o tcp." });
      return;
    }
    try {
      await Promise.all([
        updateMut.mutateAsync({
          key: KEY_HOST, value: host.trim(), value_type: "string", grupo: "ovpn",
          descripcion: "Host (DNS o IP) que los Mikrotiks usan en el .ovpn para conectarse al VPS.",
        }),
        updateMut.mutateAsync({
          key: KEY_PORT, value: portNum, value_type: "int", grupo: "ovpn",
          descripcion: "Puerto UDP del servidor OpenVPN.",
        }),
        updateMut.mutateAsync({
          key: KEY_PROTO, value: proto, value_type: "string", grupo: "ovpn",
          descripcion: "Protocolo (udp o tcp). UDP recomendado para gestión.",
        }),
      ]);
      toast.success("Configuración OVPN guardada", {
        description: "Los nuevos .ovpn descargados usarán estos valores.",
      });
      setDirty(false);
    } catch (e) {
      toast.error("Error al guardar", { description: (e as ApiError).message });
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/5 p-3 text-xs text-muted-foreground">
        <Info size={14} className="mt-0.5 shrink-0 text-primary" />
        <div>
          Estos valores aparecen en la línea <code>remote</code> de cada archivo{" "}
          <code>.ovpn</code> que descargas para los routers. Cambia el host si rotas
          el dominio del VPS o si quieres apuntar por IP en vez de DNS. Los archivos{" "}
          <code>.ovpn</code> generados antes de un cambio NO se regeneran automático;
          si modificas algo aquí, descarga un <code>.ovpn</code> fresco para cada router
          y vuelve a pegarlo en el Mikrotik.
        </div>
      </div>

      <div className="rounded-lg border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Globe size={14} className="text-primary" />
          <Label className="text-sm font-semibold">Servidor OpenVPN (remote)</Label>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="md:col-span-2 flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Host (DNS o IP)
            </label>
            <Input
              value={host}
              onChange={(e) => { setHost(e.target.value); setDirty(true); }}
              placeholder="admin.bytecom.mx"
              className="font-mono"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Puerto
            </label>
            <Input
              type="number" min={1} max={65535}
              value={port}
              onChange={(e) => { setPort(e.target.value); setDirty(true); }}
              className="font-mono"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Protocolo
          </label>
          <div className="flex gap-2">
            {(["udp", "tcp"] as const).map((p) => (
              <label key={p} className="flex items-center gap-1.5 cursor-pointer rounded border px-3 py-1.5 text-xs hover:bg-muted/30 has-[:checked]:bg-primary/10 has-[:checked]:border-primary">
                <input
                  type="radio" name="proto" value={p}
                  checked={proto === p}
                  onChange={() => { setProto(p); setDirty(true); }}
                />
                <span className="font-mono uppercase">{p}</span>
              </label>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground">
            UDP es preferido (menor latencia, sin handshake TCP-over-TCP). Solo usa
            TCP si el ISP del cliente bloquea UDP en 1194.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        {dirty && (
          <span className="text-xs text-muted-foreground">Cambios sin guardar</span>
        )}
        <Button size="sm" onClick={handleSave} disabled={!dirty || updateMut.isPending}>
          <Save size={12} />
          {updateMut.isPending ? "Guardando…" : "Guardar"}
        </Button>
      </div>
    </div>
  );
}
